/**
 * Sellox — Universal Offline Sync Queue
 *
 * Catches any failed mutation (POST/PUT/PATCH/DELETE) when the network is
 * dead, persists it to IndexedDB, then replays the queue in FIFO order
 * the moment connectivity returns. Replacement for "your click did
 * nothing" → "saved offline, will sync".
 *
 * Used by lib/api.js axios interceptor. Pages get a toast confirming
 * their action was queued; status badge in AdminLayout shows pending count.
 *
 * Excluded paths (login, sync-only endpoints) live in EXCLUDED_PATHS.
 */

import { busToast } from "./toastBus";

const DB_NAME = "sellox_sync_queue";
const STORE = "queue";
const DB_VERSION = 1;

let dbInstance = null;
let listenersBound = false;
let isSyncing = false;
const subscribers = new Set();

// Endpoints we MUST NOT queue (would be unsafe / redundant offline)
const EXCLUDED_PATHS = [
  "/auth/login",
  "/auth/tenant-login",
  "/auth/refresh",
  "/auth/logout",
  "/payment-verify/inline/",
  "/payment-verify/image/",
  "/sales-orders/offline",       // already has its own queue
  "/whatsapp/wa-web/inbound",    // server-to-server
  "/health",
  "/openapi.json",
  // 💸 AI endpoints charge credits per call — replaying them = double billing
  "/ai/shop-design/",
  "/ai/poster/",
  // 🛰️ Ops / help / stock-block calls are interactive — never replay them later
  "/ops/",
  "/help/ask",
  "/inventory/stock-blocks",
  "/ai/image/",
  "/ai/chatbot",
  "/ai-butler/",
  // 📈 rate sync / telemetry / heartbeats are idempotent background calls — replaying is pointless
  "/exchange-rates/sync-bcv",
  "/fx-alerts/",
  "/shifts/heartbeat",
  "/telemetry",
  "/analytics/",
  "/version",
  // 📄 Downloads / report generation — replaying a PDF/CSV export is pure CPU waste
  "/catalog-pdf",
  "/export",
  "/pdf",
  "/download",
  "/report/",
  "/import/template",
];

// Network-type failures are retried every 60s; cap them so a broken item can't loop forever
const MAX_NET_ATTEMPTS = 20;

function _isDownload(config) {
  const rt = String(config?.responseType || "").toLowerCase();
  return rt === "blob" || rt === "arraybuffer";
}

function _open() {
  return new Promise((resolve, reject) => {
    if (dbInstance) return resolve(dbInstance);
    if (typeof indexedDB === "undefined") return reject(new Error("no indexedDB"));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: "id" });
        os.createIndex("queued_at", "queued_at");
      }
    };
    req.onsuccess = () => { dbInstance = req.result; resolve(req.result); };
    req.onerror = () => reject(req.error);
  });
}

async function _putItem(item) {
  const db = await _open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(item);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function _delItem(id) {
  const db = await _open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function _listAll() {
  try {
    const db = await _open();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return [];
  }
}

// 🔒 Tenant guard (2026-10-31 incident: a save made in a super-admin READ-ONLY tab failed on a network blip, got queued,
// and was replayed with the owner's own token → another tenant's settings overwrote his). Never queue in read-only
// tabs, remember which tenant the write belonged to, and only replay with a token of that same tenant.
function _readonlyTab() { try { return !!sessionStorage.getItem("impersonate_readonly"); } catch { return false; } }
function _currentToken() { try { return sessionStorage.getItem("token") || localStorage.getItem("token") || localStorage.getItem("pos_token") || ""; } catch { return ""; } }
export function tokenTenant(tok) {
  try { const p = JSON.parse(atob(String(tok || "").split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))); return p.readonly ? "__readonly__" : (p.tenant_id || ""); } catch { return ""; }
}
function _isExcluded(url) {
  if (!url) return true;
  return EXCLUDED_PATHS.some((p) => url.includes(p));
}

// Server said the action was already applied earlier → duplicate, not a failure
function _isAlreadyProcessed(msg) {
  return /ya fue procesad|ya procesad|already processed|already approved|duplicad|已处理|已审批/i.test(String(msg || ""));
}

/** Notify badge listeners after queue changes */
async function _notify() {
  const items = await _listAll();
  subscribers.forEach((cb) => { try { cb(items); } catch (_) {} });
}

/** Subscribe to queue changes (returns unsubscribe) */
export function subscribeQueue(cb) {
  subscribers.add(cb);
  _listAll().then((items) => { try { cb(items); } catch (_) {} });
  return () => subscribers.delete(cb);
}

/** Snapshot the queue (synchronous best-effort returns []) */
export async function getQueueItems() {
  return _listAll();
}

/**
 * Persist a failed mutation. Called from the axios interceptor.
 * Returns true if the item was queued (caller should show "saved offline" toast),
 * false if it was rejected (excluded path, or storage failed).
 */
export async function enqueueFailedRequest(config, error) {
  try {
    if (!config) return false;
    const method = (config.method || "post").toLowerCase();
    if (!["post", "put", "patch", "delete"].includes(method)) return false;
    const url = config.url || "";
    if (_isExcluded(url) || _isDownload(config)) return false;
    if (_readonlyTab()) return false; // read-only impersonation: writes are rejected anyway — never park them for later
    const reqTok = String((config.headers || {}).Authorization || (config.headers || {}).authorization || "").replace(/^Bearer\s+/i, "") || _currentToken();
    const tenant = tokenTenant(reqTok);
    if (tenant === "__readonly__") return false;
    // 🔒 Dedupe idempotent-style actions (approve/void/confirm/... , PUT, DELETE):
    // double-taps while offline must not queue the same action twice.
    const dataStr = typeof config.data === "string" ? config.data : JSON.stringify(config.data ?? null);
    if (method === "put" || method === "delete" || /\/(approve|void|cancel|confirm|complete|close|pay|start|end)\b/.test(url)) {
      const existing = await _listAll();
      const dup = existing.some((x) => x.method === method && x.url === url &&
        ((typeof x.data === "string" ? x.data : JSON.stringify(x.data ?? null)) === dataStr));
      if (dup) return true; // already queued — treat as accepted, no duplicate
    }
    const item = {
      id: `sync_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      method,
      url,
      data: config.data,
      headers: {
        // Strip auth header — token may have refreshed by sync time
        ...(config.headers || {}),
      },
      params: config.params,
      label: _labelFor(method, url),
      queued_at: new Date().toISOString(),
      tenant_id: tenant,
      attempts: 0,
      last_error: error?.message || (error?.response?.status ? `HTTP ${error.response.status}` : "network"),
    };
    // Don't persist Authorization header — re-attached on replay from current token
    if (item.headers.Authorization) delete item.headers.Authorization;
    if (item.headers.authorization) delete item.headers.authorization;
    await _putItem(item);
    await _notify();
    return true;
  } catch (e) {
    console.warn("[syncQueue] enqueue failed:", e);
    return false;
  }
}

/** Generate a friendly Spanish/Chinese label so the user knows what's pending */
function _labelFor(method, url) {
  const u = url.replace(/^\/api/, "");
  const m = method.toUpperCase();
  if (m === "POST" && /^\/products\b/.test(u))   return "➕ Nuevo producto / 新商品";
  if (m === "PUT"  && /^\/products\//.test(u))   return "✏️ Editar producto / 编辑商品";
  if (m === "DELETE" && /^\/products\//.test(u)) return "🗑️ Eliminar producto / 删除商品";
  if (m === "POST" && /^\/inventory\/in\b/.test(u))      return "📦 Entrada de inventario / 入库";
  if (m === "POST" && /^\/inventory\/out\b/.test(u))     return "📤 Salida de inventario / 出库";
  if (m === "POST" && /^\/inventory\/adjust\b/.test(u))  return "📊 Ajuste de stock / 库存调整";
  if (m === "POST" && /^\/inventory\/transfer\b/.test(u)) return "🚚 Transferencia / 调货";
  if (m === "POST" && /^\/transfer-orders\b/.test(u))      return "🚚 Orden de transferencia / 调货单";
  if (m === "POST" && /^\/credits\b/.test(u))             return "💳 Nuevo crédito / 新赊账";
  if (/^\/credits\/.+\/(payment|pay)\b/.test(u))          return "💰 Pago de crédito / 收赊账款";
  if (m === "POST" && /^\/customers\b/.test(u))           return "👤 Nuevo cliente / 新客户";
  if (m === "PUT"  && /^\/customers\//.test(u))           return "✏️ Editar cliente / 编辑客户";
  if (m === "POST" && /^\/suppliers\b/.test(u))           return "🏭 Nuevo proveedor / 新供应商";
  if (m === "POST" && /^\/purchases\b/.test(u))           return "🛒 Compra / 采购单";
  if (m === "POST" && /^\/expenses\b/.test(u))            return "💸 Gasto / 支出";
  if (/sales-orders\/.+\/refund/.test(u))                 return "↩️ Reembolso / 退款";
  if (/sales-orders\/.+\/void/.test(u))                   return "❌ Anular venta / 作废订单";
  if (m === "POST" && /^\/shifts\/(start|end)\b/.test(u)) return "🕐 Cambio de turno / 班次";
  return `${m} ${u}`;
}

/**
 * Replay all queued items in FIFO order. Stops at the first network failure.
 * Items returning 4xx are kept and marked failed (manual user action needed).
 */
export async function flushQueue(axios, opts = {}) {
  if (isSyncing) return { skipped: true };
  isSyncing = true;
  let success = 0, failedPermanent = 0, failedNetwork = 0;
  try {
    const items = (await _listAll()).sort((a, b) => a.queued_at.localeCompare(b.queued_at));
    for (const it of items) {
      // ⛔ Items the server already REJECTED (4xx) must not be hammered on the
      // 60s auto-loop forever — they need a human decision (fix data or
      // discard). Manual "Sincronizar ahora" passes includeFailed:true.
      if (it.permanent_fail && !opts.includeFailed) continue;
      // 🧹 Items that should never have been queued (downloads / excluded paths
      // from older versions) — drop instead of replaying
      if (_isExcluded(it.url || "")) { await _delItem(it.id); continue; }
      // Pull fresh auth token — same precedence as AuthContext (per-tab session first)
      if (_readonlyTab()) continue; // never replay anything from a read-only support tab
      const token = _currentToken();
      const curTenant = tokenTenant(token);
      if (curTenant === "__readonly__") continue;
      // 🔒 Only replay with a token of the SAME tenant that queued it (cross-tenant write guard)
      if (it.tenant_id && curTenant && it.tenant_id !== curTenant) { await _delItem(it.id); continue; }
      const headers = { ...(it.headers || {}) };
      if (token) headers.Authorization = `Bearer ${token}`;
      if (it.tenant_id) headers["X-Sellox-Queued-Tenant"] = it.tenant_id; // server double-checks it matches the token
      try {
        await axios({
          method: it.method,
          url: it.url,
          data: it.data,
          params: it.params,
          headers,
          // Bypass the offline-queue interceptor so we don't re-enqueue our own replay
          __isReplay: true,
          // Bypass global error toasts during replay
          skipGlobalError: true,
        });
        await _delItem(it.id);
        success++;
      } catch (e) {
        const status = e?.response?.status;
        if (status && status >= 400 && status < 500) {
          // Permanent failure — keep item but mark it
          const d = e?.response?.data?.detail;
          const msg = typeof d === "string" ? d
            : (d && typeof d === "object" ? (d.message || JSON.stringify(d)) : "rejected");
          // ✅ "Already processed" = the action DID land on a previous attempt
          // (double-tap or retry after timeout). That's a success, not a
          // rejection — drop the duplicate silently instead of scaring the user.
          if (_isAlreadyProcessed(msg)) {
            await _delItem(it.id);
            success++;
            continue;
          }
          const firstFail = !it.permanent_fail;
          it.attempts = (it.attempts || 0) + 1;
          it.last_error = `HTTP ${status}: ${msg}`;
          it.permanent_fail = true;
          await _putItem(it);
          failedPermanent++;
          // 🔊 Tell the user LOUDLY the first time the server rejects a queued
          // action — before this fix it failed in total silence and the user
          // believed the "saved offline" toast meant success.
          if (firstFail) {
            busToast("error", `❌ Rechazado por el servidor / 服务器拒绝: ${it.label || it.url}`, {
              description: `${msg} — 该操作未生效！点右上角 ☁️ 图标查看并处理 / Revise el icono ☁️ arriba`,
              duration: 15000,
            });
          }
        } else {
          // Network/server error — stop and retry next time
          it.attempts = (it.attempts || 0) + 1;
          it.last_error = e?.message || "network";
          if (it.attempts >= MAX_NET_ATTEMPTS) {
            it.permanent_fail = true;
            it.last_error = `${it.last_error} (×${it.attempts}, detenido / 已停止重试)`;
            await _putItem(it);
            failedPermanent++;
            busToast("error", `❌ No se pudo subir tras ${it.attempts} intentos / 重试${it.attempts}次失败: ${it.label || it.url}`, {
              description: "点右上角 ☁️ 图标查看并处理 / Revise el icono ☁️ arriba",
              duration: 15000,
            });
            continue;
          }
          await _putItem(it);
          failedNetwork++;
          break;  // Stop replay loop, will retry on next online event
        }
      }
    }
  } finally {
    isSyncing = false;
    await _notify();
  }
  return { success, failedPermanent, failedNetwork };
}

/** Manually discard a single queued item (e.g. user gave up) */
export async function discardItem(id) {
  await _delItem(id);
  await _notify();
}

/** Wire up auto-sync on `online` event + on app start (once) */
export function bindAutoSync(axios) {
  if (listenersBound) return;
  listenersBound = true;
  // 🔔 Startup warning: if this PC still holds unsynced or rejected offline
  // actions from a previous session, tell the user right away (once per
  // session) — nobody should assume yesterday's payments went through.
  setTimeout(() => {
    // 🛍️ Public/customer-facing pages (webshop, tracking, statements) must NOT
    // show POS offline-sync warnings — those belong to the cashier UI only.
    try {
      const p = window.location.pathname;
      if (p === "/" || /^\/(shop\/|track\/|aparta|customer-statement|employee-statement|catalog|pickup|member)/.test(p)) return;
    } catch (_e) { /* SSR safety */ }
    _listAll().then(async (items) => {
      // 🧹 Auto-heal: drop stale "already processed" rejections from earlier
      // versions — the action DID succeed, these entries only scare the user.
      const stale = items.filter((it) => (it.permanent_fail && _isAlreadyProcessed(it.last_error)) || _isExcluded(it.url || it.config?.url || ""));
      if (stale.length) {
        for (const it of stale) await _delItem(it.id);
        items = items.filter((it) => !stale.includes(it));
        await _notify();
      }
      if (!items.length || sessionStorage.getItem("sync_warn_shown")) return;
      sessionStorage.setItem("sync_warn_shown", "1");
      const failed = items.filter((it) => it.permanent_fail).length;
      const pend = items.length - failed;
      if (failed > 0) {
        busToast("error", `⚠️ 本机有 ${failed} 笔操作被服务器拒绝 / ${failed} rechazadas`, {
          description: `这些操作没有生效！${pend > 0 ? `另有 ${pend} 笔等待上传。` : ""}点右上角 ☁️ 图标查看和处理`,
          duration: 20000,
        });
      } else {
        busToast("warning", `📡 本机有 ${pend} 笔操作还没上传到服务器 / ${pend} pendientes`, {
          description: "联网后会自动上传。点右上角 ☁️ 图标可查看明细",
          duration: 12000,
        });
      }
    }).catch(() => {});
  }, 2500);
  // Initial flush a few seconds after start, in case items were queued from a
  // previous session and the user opened the app while online.
  setTimeout(() => {
    if (typeof navigator === "undefined" || navigator.onLine) flushQueue(axios);
  }, 4000);
  if (typeof window !== "undefined") {
    window.addEventListener("online", () => flushQueue(axios));
    // Periodic retry every 20s while there are pending items (fast sync)
    setInterval(() => {
      if (!navigator.onLine) return;
      _listAll().then((items) => { if (items.some((it) => !it.permanent_fail)) flushQueue(axios); });
    }, 20000);
  }
}
